import { AnalyticsEventName } from '@/shared/config';
import type { FullHeadMPRPaneLayout } from '@/shared/graphics/medicalImageRender/MedicalImageRender';
import { IconNames } from '@/shared/ui';

import {
  MPRAnalyticsScreen,
  MPRAnnotationTool,
  MPRShortcut,
  MPRTool,
} from './types';

export const MPR_TOOL_ICON_SIZE = 25;
export { FULL_HEAD_MPR_PLANE_TOLERANCE } from '@/shared/graphics/medicalImageRender/MedicalImageRender';

export const DEFAULT_FULL_HEAD_MPR_PANE_LAYOUT: FullHeadMPRPaneLayout =
  'one_plus_two';

export const FULL_HEAD_MPR_PANE_LAYOUTS: FullHeadMPRPaneLayout[] = [
  'one_plus_two',
  'three_columns',
];

export const MPR_PANE_LAYOUT_ICONS: Record<FullHeadMPRPaneLayout, IconNames> = {
  one_plus_two: 'layoutOnePlusTwo',
  three_columns: 'layoutThreeColumns',
};

export const MPR_NEXT_PANE_LAYOUT: Record<
  FullHeadMPRPaneLayout,
  FullHeadMPRPaneLayout
> = {
  one_plus_two: 'three_columns',
  three_columns: 'one_plus_two',
};

export {
  FULL_HEAD_MPR_PROJECTION_TO_ORIENTATION,
  FULL_HEAD_MPR_PROJECTION_TO_SERIES,
  ORIENTATION_TO_SERIES,
  SERIES_TO_ORIENTATION,
} from '@/shared/graphics/medicalImageRender/MedicalImageRender';

export const MPR_TOOLS_BY_MODE_MAP: Record<MPRTool, IconNames> = {
  mainMode: 'move2',
  contrastBrightnessMode: 'brightness',
  panMode: 'movepawFilled',
  zoomMode: 'zoom',
  scrollThroughVolumeMode: 'split',
  rotateMode: 'turn',
  rotateAxesMode: 'rotateAxes',
  reset: 'rotate',
};

export const MPR_TOOLBAR_GROUPS = [
  [
    'mainMode',
    'panMode',
    'zoomMode',
    'scrollThroughVolumeMode',
    'rotateMode',
    'rotateAxesMode',
  ],
  ['contrastBrightnessMode', 'sharpness'],
  ['ruler', 'angle', 'arrow', 'polyline', 'deleteSelectedAnnotation'],
  ['paneLayout', 'download'],
  ['reset'],
] as const;

export const MPR_TOOL_SHORTCUTS: Partial<Record<MPRTool, MPRShortcut>> = {
  contrastBrightnessMode: { code: 'KeyW', label: 'W' },
  panMode: { code: 'KeyM', label: 'M' },
  zoomMode: { code: 'KeyZ', label: 'Z' },
  scrollThroughVolumeMode: { code: 'KeyB', label: 'B' },
  rotateMode: { code: 'KeyR', label: 'R' },
  rotateAxesMode: { code: 'KeyT', label: 'T' },
  reset: { code: 'Digit0', label: '0 / O', aliases: ['Numpad0', 'KeyO'] },
};

export const MPR_AXES_SHORTCUT: MPRShortcut = { code: 'KeyX', label: 'X' };
export const MPR_SHARPNESS_SHORTCUT: MPRShortcut = { code: 'KeyS', label: 'S' };
export const MPR_FINISH_ANNOTATION_SHORTCUT: MPRShortcut = {
  code: 'Enter',
  label: 'Enter',
};

export const MPR_ARROW_CONTROL_SELECTOR =
  'input, [role="slider"], [role="spinbutton"], [role="radio"], [role="tab"], [role="menu"], [role="listbox"], [role="toolbar"]';

export const MPR_ANNOTATION_SHORTCUTS: Record<MPRAnnotationTool, MPRShortcut> =
  {
    ruler: { code: 'KeyL', label: 'L' },
    angle: { code: 'KeyA', label: 'A' },
    arrow: { code: 'KeyY', label: 'Y' },
    polyline: { code: 'KeyP', label: 'P' },
  };

export const MPR_ANNOTATIONS_VISIBILITY_SHORTCUT: MPRShortcut = {
  code: 'KeyH',
  label: 'H',
};

export const MPR_TOOL_EVENTS: Record<
  MPRAnalyticsScreen,
  Record<MPRTool, AnalyticsEventName>
> = {
  toothPage: {
    mainMode: 'ToothPageMPRAxesButtonClicked',
    contrastBrightnessMode: 'ToothPageMPRBrightnessContrastButtonClicked',
    panMode: 'ToothPageMPRPanButtonClicked',
    zoomMode: 'ToothPageMPRZoomButtonClicked',
    scrollThroughVolumeMode: 'ToothPageMPRScrollButtonClicked',
    rotateMode: 'ToothPageMPRRotateButtonClicked',
    rotateAxesMode: 'ToothPageMPRRotateAxesButtonClicked',
    reset: 'ToothPageMPRResetButtonClicked',
  },
  reportPage: {
    mainMode: 'ReportPageMPRAxesButtonClicked',
    contrastBrightnessMode: 'ReportPageMPRBrightnessContrastButtonClicked',
    panMode: 'ReportPageMPRPanButtonClicked',
    zoomMode: 'ReportPageMPRZoomButtonClicked',
    scrollThroughVolumeMode: 'ReportPageMPRScrollButtonClicked',
    rotateMode: 'ReportPageMPRRotateButtonClicked',
    rotateAxesMode: 'ReportPageMPRRotateAxesButtonClicked',
    reset: 'ReportPageMPRResetButtonClicked',
  },
};

export const MPR_TOGGLE_EVENTS: Record<
  MPRAnalyticsScreen,
  {
    axes: AnalyticsEventName;
    imageOptionsSync: AnalyticsEventName;
    zoomSync: AnalyticsEventName;
    paneLayout: AnalyticsEventName;
  }
> = {
  toothPage: {
    axes: 'ToothPageMPRAxesToggleClicked',
    imageOptionsSync: 'ToothPageMPRBrightnessContrastSyncToggleClicked',
    zoomSync: 'ToothPageMPRZoomSyncToggleClicked',
    paneLayout: 'ToothPageMPRPaneLayoutSelected',
  },
  reportPage: {
    axes: 'ReportPageMPRAxesToggleClicked',
    imageOptionsSync: 'ReportPageMPRBrightnessContrastSyncToggleClicked',
    zoomSync: 'ReportPageMPRZoomSyncToggleClicked',
    paneLayout: 'ReportPageMPRPaneLayoutSelected',
  },
};
